<template>
    <div class="chart-holder">
        <div class="highcharts" ref="chartRef" />
        <font-awesome-icon
            v-if="props.isUsingBoost"
            :icon="['fas', 'rabbit-running']"
            class="boosted-icon"
            v-tooltip.top="'This chart has reduced functionality (including no live updates) due to the number of points. \n You can override this in your User Settings.'"
        />
    </div>
</template>
<script setup lang="ts">
import {
    ref, onMounted, onBeforeUnmount,
    watch,
} from 'vue';
import Highcharts, {
    Chart, Options, Series, SeriesOptionsType, type Point,
} from 'highcharts';

import More from 'highcharts/highcharts-more';
import Exporting from 'highcharts/modules/exporting';
import Offline from 'highcharts/modules/offline-exporting';
import stockInit from 'highcharts/modules/stock';
import Treemap from 'highcharts/modules/treemap';
import Histogram from 'highcharts/modules/histogram-bellcurve';
import Boost from 'highcharts/modules/boost';

import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { IPointData } from '@/interfaces/general/IPointData.ts';
import { useUIStore } from '@/stores/uiStore.ts';
import { getTheme, transparentBackgroundTheme } from '@/utils/chartGlobalSettings.ts';
import { binarySearchClosest } from '@/utils/numberUtils.ts';

More(Highcharts);
Exporting(Highcharts);
Offline(Highcharts);
stockInit(Highcharts);
Treemap(Highcharts);
Boost(Highcharts);
Histogram(Highcharts);

// Props
const props = defineProps<{
    initialOptions: Options;
    constructorType: keyof typeof Highcharts;
    callback?:() => void;
    updateArgs?: boolean[];
    isUsingBoost?: boolean;
    transparentBg?: boolean;
    skipAutomaticThemeUpdate?: boolean;
}>();

const userSettingsStore = useUserSettingsStore();
const uiStore = useUIStore();

const emit = defineEmits<{
    'hasBoost': [boolean];
    'rerequest': [];
}>();

const chartRef = ref(null);
const chartInstance = ref<Chart | null>(null);
const isFullScreen = ref<boolean>(false);

function checkForSingleXColumn (chart: Options): boolean {
    if (!chart.series) return false;

    return chart.series.some(series => {
        if ('data' in series && Array.isArray(series.data)) {
            if (series.data.length === 1) return true;
        }
        return false;
    });
}

function makeChart (options: Options): void {
    if (checkForSingleXColumn(options)) {
        options.plotOptions = options.plotOptions || {};
        options.plotOptions.line = options.plotOptions.line || {};
        options.plotOptions.line.marker = {
            enabled: true,
        };
    }
    chartInstance.value = (Highcharts)[props.constructorType](
        chartRef.value,
        { ...options },
        props.callback ? props.callback : null,
    );
    updateBoost();
}

function updateOptions (newOptions: Options): void {
    chartInstance.value?.update({ ...newOptions }, ...(props.updateArgs || [true, true]));
    updateBoost();
}

function updateThemeOnly (themeOptions: Options): void {
    if (!chartInstance.value) return;
    chartInstance.value.update(themeOptions, false, true);
    if (props.transparentBg) {
        chartInstance.value.update(transparentBackgroundTheme, false, true);
    }
    updateBoost();
}

function updateBoost (): void {
    if (!chartInstance.value) return;
    const isBoosted = chartInstance.value.series.some((s) => (s as unknown as {boosted: boolean}).boosted);
    emit('hasBoost', isBoosted);
}

function getSeriesIndex (id: string): undefined | number {
    return chartInstance.value?.series.findIndex(s => s.userOptions.id === id);
}

function getPointToAdd (point: IPointData, series: Series): Highcharts.PointOptionsObject[] | Highcharts.PointOptionsObject | null | undefined {
    const options = series.options as SeriesOptionsType;

    if (!('data' in options)) return null;

    if (point.byPoint) {
        if (Array.isArray(options.data) && Array.isArray(options.data[0])) {
            return point.arrayPoint;
        }
        return point.objectPoint;
    }

    return point.arrayPoint;
}

function addPoint (index: number, point: IPointData): boolean {
    if (index === -1 || !chartInstance.value) return false;
    const series = chartInstance.value.series[index];
    const pointCorrected = getPointToAdd(point, series);
    series.addPoint(pointCorrected as Highcharts.PointOptionsObject, false, false);
    return true;
}

function updatePoint (index: number, point: IPointData): boolean {
    if (index === -1 || !chartInstance.value) return false;

    const series = chartInstance.value.series[index];
    const pointCorrected = getPointToAdd(point, series);
    const pointToCheck = Array.isArray(pointCorrected) ? pointCorrected[0] : (pointCorrected as Highcharts.PointOptionsObject).x;

    const targetPoint = series.points.find(p => p.x === pointToCheck);

    if (!targetPoint) return false;

    if (series.data && series.data.length !== 0) {
        targetPoint.update(pointCorrected as Array<Highcharts.PointOptionsObject>, false);
    } else {
        emit('rerequest');
    }

    return true;
}

function deletePoint (index: number, point: IPointData): boolean {
    if (index === -1) return false;
    if (!chartInstance.value) return false;
    const series = chartInstance.value.series[index];
    const pointCorrected = getPointToAdd(point, series);
    const pointToCheck = Array.isArray(pointCorrected) ? pointCorrected[0] : (pointCorrected as Highcharts.PointOptionsObject).x;

    const pointsToRemove = series.points.filter(p => p.x === pointToCheck);
    pointsToRemove.forEach(p => p.remove(false));
    return pointsToRemove.length > 0;
}

function removeOldPoints (cutoffTime: number, index: number): void {
    if (!chartInstance.value) return;

    const series = chartInstance.value.series[index];
    const pointsToRemove = series.points.filter(point => point.x < cutoffTime);

    pointsToRemove.forEach(point => point.remove(false));
}

function setXAxisExtremes (minX: (number | undefined) = undefined, maxX: (number | undefined) = undefined): void {
    if (!chartInstance.value) return;
    const xAxis = chartInstance.value.xAxis;
    if (xAxis.length > 1) {
        xAxis[0].setExtremes(minX, maxX, true);
        xAxis[1].setExtremes(minX, maxX, true);
    } else {
        xAxis[0].setExtremes(minX, maxX, true);
    }
}

function setFullscreenOptions (): void {
    if (!chartInstance.value) return;
    isFullScreen.value = !isFullScreen.value;

    if (isFullScreen.value) {
        chartInstance.value.update({
            tooltip: { outside: false },
        });
        chartInstance.value.container.style.backgroundColor = uiStore.cssVariables.chartBackground;
    } else {
        chartInstance.value.update({
            tooltip: { outside: props.initialOptions.tooltip?.outside ?? true },
        });
        chartInstance.value.container.style.backgroundColor = 'transparent';
    }
}

function redraw (): void {
    chartInstance.value?.redraw();
}

function applySharedSeriesVisibility (visibilityMap: Record<string, boolean>): void {
    if (!chartInstance.value) return;

    chartInstance.value.series.forEach((series: Series) => {
        const userOptions = series.userOptions as { id?: string };
        const seriesId = userOptions?.id;

        if (seriesId && seriesId in visibilityMap) {
            series.setVisible(visibilityMap[seriesId], false);
        }
    });

    chartInstance.value.redraw();
}

function resize (): void {
    chartInstance.value?.reflow();
}

function fullscreen (): void {
    if (!chartInstance.value) return;
    chartInstance.value.fullscreen.open();
}

function exportChart (): void {
    if (!chartInstance.value) return;
    chartInstance.value.options.exporting = {
        ...chartInstance.value.options.exporting,
        chartOptions: getTheme(uiStore.colourPaletteId),
    };
    chartInstance.value.exportChartLocal();
}

function printChart (): void {
    chartInstance.value?.print();
}

let rafId: number | null = null;
let latestHoverDate: number | undefined;
let previousPoints: Point[] = [];
const ONE_HOUR_MS = 3600000;

function triggerTooltipHover (hoverDate: number | undefined): void {
    if (!chartInstance.value) return;

    latestHoverDate = hoverDate;

    if (rafId !== null) return;

    rafId = requestAnimationFrame(() => {
        rafId = null;

        const chart = chartInstance.value as Chart;

        const undraw = () : void => {
            chart.tooltip.hide(0);
            chart.xAxis[0].drawCrosshair(undefined, undefined);
            chart.hoverPoint?.setState();
        };

        if (previousPoints.length > 0) {
            previousPoints.forEach(p => p.setState()); // undefined = 'normal' state
            previousPoints = [];
        }

        if (latestHoverDate === undefined) {
            undraw();
            return;
        }

        const pointsToHighlight: Point[] = [];

        const searchCache = new Map<number[], number>();

        chart.series.forEach((s) => {
            const series = s as Series & { processedXData: number[]; points: Point[] };
            if (!series.visible) return;

            const xData = series.processedXData;
            if (!xData || xData.length === 0) return;

            let closestIndex = searchCache.get(xData);

            if (closestIndex === undefined) {
                closestIndex = binarySearchClosest(xData, latestHoverDate!);

                if (xData[closestIndex] > latestHoverDate! && closestIndex > 0) {
                    closestIndex -= 1;
                }

                searchCache.set(xData, closestIndex);
            }

            const closestXValue = xData[closestIndex];

            const distance = Math.abs(closestXValue - latestHoverDate!);

            if (distance <= ONE_HOUR_MS) {
                const point = series.points[closestIndex];
                if (point) pointsToHighlight.push(point);
            }
        });

        if (pointsToHighlight.length > 0) {
            pointsToHighlight.forEach(p => p.setState('hover'));
            chart.tooltip.refresh(pointsToHighlight);
            chart.xAxis[0].drawCrosshair(undefined, pointsToHighlight[0]);
            previousPoints = pointsToHighlight;
        } else {
            undraw();
        }
    });
}

function setHighchartsWrap (): void {
    Highcharts.wrap(Highcharts.Tooltip.prototype, 'refresh', function refreshTooltip (this: Highcharts.Tooltip, proceed) {
        const args = arguments; // eslint-disable-line prefer-rest-params
        const points = args[1];
        const point = Array.isArray(points) ? points[0] : points;
        const chart = point.series.chart;

        if ('boostResizeTarget' in chart) {
            proceed.apply(this, [].slice.call(args, 1));
            return;
        }

        chart.series.forEach((series: Series) => {
            if (series === point.series || !series.visible) return;

            let closest; let dist; let distance = Number.MAX_VALUE; let
                allNullFuture = true;

            series.points.forEach(p => {
                dist = point.x - p.x;
                if (dist < distance && p.y !== null && dist >= 0) {
                    distance = dist;
                    closest = p;
                }
                if (p.y !== null && p.x >= point.x) {
                    allNullFuture = false;
                }
            });

            if (!allNullFuture && closest && args[1].indexOf(closest) === -1) {
                if (!Array.isArray(args[1])) args[1] = [args[1]];
                args[1].push(closest);
            }
        });

        proceed.apply(this, [].slice.call(args, 1));
    });
}

watch(() => uiStore.colourPaletteRevision, () => {
    if (props.skipAutomaticThemeUpdate || !chartInstance.value) return;
    updateThemeOnly(Highcharts.theme as Options);
});

onMounted(() => {
    if (props.initialOptions && Highcharts[props.constructorType as keyof typeof Highcharts]) {
        makeChart(props.initialOptions);
    }

    if (userSettingsStore.userSettings.otherSettings.data.showNearestPointOnTooltip) {
        setHighchartsWrap();
    }

    window.addEventListener('fullscreenchange', setFullscreenOptions);
    window.addEventListener('mozfullscreenchange', setFullscreenOptions);
    window.addEventListener('webkitfullscreenchange', setFullscreenOptions);
    window.addEventListener('msfullscreenchange', setFullscreenOptions);
});

onBeforeUnmount(() => {
    if (chartInstance.value) {
        chartInstance.value.destroy();
    }

    window.removeEventListener('fullscreenchange', setFullscreenOptions);
    window.removeEventListener('mozfullscreenchange', setFullscreenOptions);
    window.removeEventListener('webkitfullscreenchange', setFullscreenOptions);
    window.removeEventListener('msfullscreenchange', setFullscreenOptions);
});

defineExpose({
    fullscreen, updateOptions, updateThemeOnly, getSeriesIndex, addPoint, updatePoint, deletePoint, removeOldPoints, setXAxisExtremes, redraw, resize, exportChart, printChart, triggerTooltipHover, applySharedSeriesVisibility, chartInstance,
});

</script>

<style lang="scss" scoped>
.chart-holder {
    position: relative;
    width: 100%;
    height: 100%;
    .highcharts {
        height: 100%;
    }
    .boosted-icon {
        position: absolute;
        top: var(--defaultSpacing);
        right: var(--defaultSpacing);
        font-size: var(--largeFontSize);
        color: var(--lcpWatermelon);
    }
}
</style>
